import * as React from 'react';
import cx from 'classnames';
import { groupBy } from 'lodash';
import * as moment from 'moment';

import ProgressBar from '@magoosh/lib/progress_bar';
import Tabs from '@magoosh/gre/adaptive_prep_v1/lib/tabs';

import style from './style.module.scss';

interface Props {
  activities: {
    createdAt: number;
    data: any;
    id: number;
    name: string;
    type: 'CustomPractice' | 'MockTest' | 'StudyPlan';
  }[];
}

const StudyActivityTimeline: React.FC<Props> = (props) => {
  const renderRows = (rows, frequencyFactor) => {
    const color = {
      5: '#4D2079',
      4: '#4D2079',
      3: '#9b51e0',
      2: '#9b51e0',
      1: '#cb97fc'
    }[frequencyFactor];

    return (
      <div>
        {rows.map((row, idx) => {
          const progressLabelColor = row.answerSet.percentCorrect === 0 ? '#817E85' : '#FFFFFF';
          const progressBarColor = row.answerSet.percentCorrect === 0 ? '#EEEEEF' : color;

          return (
            <div className="d-flex u-margin-V-xs" key={`${frequencyFactor}-${idx}`}>
              <div style={{ flex: 1 }}>
                {row.name}
                <div className="small u-margin-T-n">
                  Count: {row.answerSet.size}, Difficulty: {row.answerSet.difficulty}, Level: {row.score}/10
                  {row.isAssumed ? ' (assumed)' : null}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <ProgressBar
                  backgroundColor="#EEEEEF"
                  color={progressBarColor}
                  minLabel={row.answerSet.size === 0 ? 'N/A' : `${row.answerSet.percentCorrect}%`}
                  minLabelStyle={{
                    display: 'flex',
                    alignSelf: 'flex-start',
                    paddingLeft: '10px',
                    color: progressLabelColor
                  }}
                  percentage={row.answerSet.percentCorrect === 0 ? 100 : row.answerSet.percentCorrect}
                  size="lg"
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCustomPractice = (customPractice) => {
    return (
      <div>
        <h2 className="u-margin-B-n">{customPractice.name}</h2>
        <small>{moment.unix(customPractice.createdAt).format('MM/DD/YYYY')}</small>
        <ul>
          <li>{customPractice.data.settings.subjectTags.join(', ')}</li>
          <li>
            {customPractice.data.answers.attempted} prompts, {customPractice.data.answers.correct} correct
          </li>
        </ul>
      </div>
    );
  };

  const renderStudentPerformance = (studentPerformance) => {
    const frequencyBuckets = groupBy(studentPerformance, (row) => row.frequencyFactor);

    return (
      <>
        {Object.keys(frequencyBuckets)
          .reverse()
          .map((frequencyFactor, idx) => {
            const rows = frequencyBuckets[frequencyFactor];

            return (
              <div key={frequencyFactor}>
                {idx !== 0 && <hr />}
                {renderRows(
                  rows.sort((a, b) => b.answerSet.percentCorrect - a.answerSet.percentCorrect),
                  frequencyFactor
                )}
              </div>
            );
          })}
      </>
    );
  };

  const renderMockTest = (mockTest) => {
    return (
      <div>
        <h2 className="u-margin-B-n">
          {mockTest.name} (Math {mockTest.data.scores.math} / Verbal {mockTest.data.scores.verbal})
        </h2>
        <small>{moment.unix(mockTest.createdAt).format('MM/DD/YYYY')}</small>
      </div>
    );
  };

  const renderStudyPlan = (studyPlan) => {
    return (
      <div>
        <h2 className="u-margin-B-n">{studyPlan.name}</h2>
        <small>{moment.unix(studyPlan.createdAt).format('MM/DD/YYYY')}</small>

        <div className="d-flex">
          <div className="u-margin-R-l" style={{ flex: 1 }}>
            {studyPlan.data.studyPlanItems.map((item) => {
              return (
                <div
                  className={cx('u-border-A-s u-padding-A-xs u-margin-T-s', {
                    [style.complete]: item.status === 'complete',
                    [style.inProgress]: item.status === 'started'
                  })}
                  key={item.id}>
                  <div className="d-flex justify-content-between small">
                    <div>
                      {item.section} {'>'} {item.type} {'>'} {item.status}
                    </div>

                    {item.type === 'Segment' ? (
                      <div>
                        L: {item.progress.lessons.completed}/{item.progress.lessons.available}, P:{' '}
                        {item.progress.practices.completed}/{item.progress.practices.available}
                      </div>
                    ) : null}
                  </div>

                  <div>
                    <strong>{item.name}</strong>
                    {item.addedBy === 'student' && <small> (added by student)</small>}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ flex: 2 }}>
            <Tabs defaultTab="math">
              <Tabs.Tab tab="math">Math</Tabs.Tab>
              <Tabs.Tab tab="verbal">Verbal</Tabs.Tab>

              <div>
                <small>* Overall student performance when this study plan was generated</small>
              </div>

              <Tabs.Panel tab="math">
                {renderStudentPerformance(studyPlan.data.studentPerformance.math)}
              </Tabs.Panel>

              <Tabs.Panel tab="verbal">
                {renderStudentPerformance(studyPlan.data.studentPerformance.verbal)}
              </Tabs.Panel>
            </Tabs>
          </div>
        </div>
      </div>
    );
  };

  const studyItemStatus = (item) => {
    if (item.completedAt) {
      return 'Complete';
    } else if (item.startedAt) {
      return 'Started';
    }
  };

  return (
    <div>
      {props.activities.map((activity, idx) => {
        return (
          <div className="u-margin-B-xl" key={idx}>
            {activity.type === 'StudyPlan' && renderStudyPlan(activity)}
            {activity.type === 'MockTest' && renderMockTest(activity)}
            {activity.type === 'CustomPractice' && renderCustomPractice(activity)}
          </div>
        );
      })}
    </div>
  );
};

export default StudyActivityTimeline;
